import {
  LastRunSuccessRateHeader,
  PerformanceCardDetails,
  PieChartCard,
} from '@/components';
import { useGetChartColorsAndLabel } from '@/hooks';
import { getLastRunSucessRateColumns } from '@/modules/Automations/pages/AutomationsPage/utils';
import { useGetProcess } from '@/modules/Processes/queries';
import { useProcessLastRun } from '@/modules/Processes/queries/use-process-automations-performance';
import { ExceptionType, ResultFilter, Success } from '@/shared/types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ProcesssLastRunSuccessRateCardProps = {
  processId: number;
};

export const ProcesssLastRunSuccessRateCard = ({
  processId,
}: ProcesssLastRunSuccessRateCardProps) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>();
  const [selectedResult, setSelectedResult] = useState<ResultFilter>();

  const { rateChart } = useGetChartColorsAndLabel();

  const { data: process, isLoading } = useGetProcess(processId);

  const {
    data,
    isLoading: isLoadingLastRun,
    fetchNextPage: fetchLastRunNextPage,
    isFetching,
    isFetchingNextPage,
  } = useProcessLastRun({
    processId,
    enabled: isDetailsOpen,
    filter: search,
    caseResult: selectedResult === Success.Success ? true : undefined,
    exceptionType:
      !!selectedResult &&
      [ExceptionType.Business, ExceptionType.Generic].includes(selectedResult)
        ? selectedResult
        : undefined,
  });

  const lastRunSuccessRate = useMemo(
    () => data?.pages.flatMap((page) => page) || [],
    [data?.pages],
  );

  const totalMetrics = useMemo(() => {
    const {
      lastRunTotalBusinessExceptions,
      lastRunTotalSystemExceptions,
      lastRunTotalSuccessful,
    } = process?.executionMetrics ?? {
      lastRunTotalBusinessExceptions: 0,
      lastRunTotalSystemExceptions: 0,
      lastRunTotalSuccessful: 0,
    };

    return {
      totalCases:
        lastRunTotalSuccessful +
        lastRunTotalBusinessExceptions +
        lastRunTotalSystemExceptions,
      businessErrors: lastRunTotalBusinessExceptions,
      genericErrors: lastRunTotalSystemExceptions,
      successfulCases: lastRunTotalSuccessful,
      runStartDate: process?.executionMetrics.lastRunStartDate,
      runEndDate: process?.executionMetrics.lastRunEndDate,
    };
  }, [process]);

  return (
    <>
      <PieChartCard
        data={[
          {
            name: rateChart.successes.name,
            fill: rateChart.successes.fill,
            value: process?.executionMetrics.lastRunTotalSuccessful ?? 0,
          },
          {
            name: rateChart.businessErrors.name,
            fill: rateChart.businessErrors.fill,
            value:
              process?.executionMetrics.lastRunTotalBusinessExceptions ?? 0,
          },
          {
            name: rateChart.genericErrors.name,
            fill: rateChart.genericErrors.fill,
            value: process?.executionMetrics.lastRunTotalSystemExceptions ?? 0,
          },
        ]}
        chartTitle={t('last_run_success_rate')}
        valueType="percentage"
        subtitle={t('cases')}
        onExpand={() => setIsDetailsOpen(true)}
      />
      {isDetailsOpen && (
        <PerformanceCardDetails
          title={t('last_run_success_rate')}
          subTitle={t('cases')}
          description={t('last_run_success_rate_description')}
          columns={getLastRunSucessRateColumns()}
          onClose={() => setIsDetailsOpen(false)}
          data={{
            items: lastRunSuccessRate,
            totalPages: 1,
            totalRows: lastRunSuccessRate.length,
          }}
          metrics={
            !isLoading && (
              <LastRunSuccessRateHeader
                key="lastRunHeader"
                metrics={totalMetrics}
              />
            )
          }
          fetchNextPage={fetchLastRunNextPage}
          contextType="lastRun"
          selectedResult={selectedResult}
          setSelectedResult={setSelectedResult}
          isLoading={isLoadingLastRun}
          setSearchInput={setSearch}
          isFetching={isFetching}
          isFetchingNextPage={isFetchingNextPage}
        />
      )}
    </>
  );
};
